import React, { useEffect } from "react";
import { 
  Button, 
  ActionIcon, 
  Group, 
  Stack, 
  TextInput, 
  Select, 
  Checkbox, 
  Card, 
  Text, 
  Divider,
  Loader,
  Center
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconPlus, IconTrash, IconDeviceFloppy } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

import { InputType, ServiceFormConfiguration, FormField } from "@customTypes/forms";
import { useGetFormConfig } from "@hooks/queries/forms/useGetFormConfig";
import { useSaveFormConfig } from "@hooks/mutations/forms/useSaveFormConfig";
import { useDeleteFormConfig } from "@hooks/mutations/forms/useDeleteFormConfig";

interface CustomFormBuilderProps {
  serviceId: string;
}

export const CustomFormBuilder: React.FC<CustomFormBuilderProps> = ({ serviceId }) => {
  const { t } = useTranslation();
  const { data: existingConfig, isLoading } = useGetFormConfig(serviceId);
  const { mutate: saveConfig, isLoading: isSaving } = useSaveFormConfig();
  const { mutate: deleteConfig, isLoading: isDeleting } = useDeleteFormConfig();

  const form = useForm<ServiceFormConfiguration>({
    initialValues: {
      service_id: serviceId,
      fields: [],
    },
  });

  useEffect(() => {
    if (existingConfig) {
      form.setValues(existingConfig);
    } else {
      form.setValues({
        service_id: serviceId,
        fields: [],
      });
    }
  }, [existingConfig, serviceId]);

  const addField = () => {
    const newField: FormField = {
      id: `field_${Date.now()}`,
      label: "",
      input_type: InputType.TEXT,
      required: false,
      placeholder: "",
    };
    form.insertListItem("fields", newField);
  };

  const removeField = (index: number) => {
    form.removeListItem("fields", index);
  };

  const handleSubmit = (values: ServiceFormConfiguration) => {
    saveConfig(values);
  };

  const handleDelete = () => {
    deleteConfig(serviceId);
  };

  if (isLoading) {
    return (
      <Center h={200}>
        <Loader size="md" />
      </Center>
    );
  }

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="md" mt="md">
        <Divider label={t("form-fields")} labelPosition="center" />

        {form.values.fields.map((_, index) => (
          <Card key={index} withBorder p="sm" radius="md">
            <Stack gap="xs">
              <Group justify="space-between">
                <Text fw={500} size="sm">
                  {t("field-n", { n: index + 1 })}
                </Text>
                <ActionIcon color="red" variant="subtle" onClick={() => removeField(index)}>
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>

              <Group grow align="flex-start">
                <TextInput
                  placeholder={t("field-label")}
                  label={t("label")}
                  required
                  {...form.getInputProps(`fields.${index}.label`)}
                />
                <Select
                  label={t("input-type")}
                  data={[
                    { label: t("text"), value: InputType.TEXT },
                    { label: t("email"), value: InputType.EMAIL },
                    { label: t("number"), value: InputType.NUMBER },
                    { label: t("date"), value: InputType.DATE },
                    { label: t("dropdown"), value: InputType.DROPDOWN },
                    { label: t("textarea"), value: InputType.TEXTAREA },
                    { label: t("file-upload"), value: InputType.FILE },
                  ]}
                  {...form.getInputProps(`fields.${index}.input_type`)}
                />
              </Group>

              <Group grow>
                <TextInput
                  placeholder={t("placeholder-text")}
                  label={t("placeholder")}
                  {...form.getInputProps(`fields.${index}.placeholder`)}
                />
                <Center mt="md">
                  <Checkbox
                    label={t("required")}
                    {...form.getInputProps(`fields.${index}.required`, { type: "checkbox" })}
                  />
                </Center>
              </Group>

              {form.values.fields[index].input_type === InputType.DROPDOWN && (
                 <Stack gap="xs" mt="xs">
                    <Text size="xs" fw={500}>{t("options-comma-separated")}</Text>
                    <TextInput 
                      placeholder="Option 1, Option 2, Option 3"
                      onChange={(e) => {
                        const options = e.target.value.split(",").map(opt => ({
                          label: opt.trim(),
                          value: opt.trim().toLowerCase().replace(/\s+/g, '_')
                        })).filter(opt => opt.label !== "");
                        form.setFieldValue(`fields.${index}.options`, options);
                      }}
                      defaultValue={form.values.fields[index].options?.map(o => o.label).join(", ")}
                    />
                 </Stack>
              )}
            </Stack>
          </Card>
        ))}

        <Button 
          variant="outline" 
          leftSection={<IconPlus size={16} />} 
          onClick={addField}
          fullWidth
        >
          {t("add-field")}
        </Button>

        <Group grow mt="xl">
          {existingConfig && (
            <Button 
              type="button" 
              color="red"
              variant="light"
              leftSection={<IconTrash size={16} />} 
              loading={isDeleting}
              onClick={handleDelete}
            >
              {t("delete-form-configuration")}
            </Button>
          )}
          <Button 
            type="submit" 
            leftSection={<IconDeviceFloppy size={16} />} 
            loading={isSaving}
          >
            {t("save-form-configuration")}
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
